import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
export default class BlogPage extends Page {
  oninit(vnode) {
    super.oninit(vnode);
    this.articles = [];
    this.loading = true;
    this.error = null;
    this.selectedCategory = 'all';
    this.searchQuery = '';
    this.pageSize = 24;
    this.offset = 0;
    this.hasMore = true;
    this.loadingMore = false;
    this.loadMoreError = null;
    this.loadArticles();
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    app.setTitle('Saudi OFW Blog: Qiwa, Iqama & Labor Guides');
  }
  fetchArticles(offset) {
    return app.store.find('discussions', {
      filter: {
        tag: 'ofw-blog'
      },
      include: 'firstPost,tags',
      page: {
        limit: this.pageSize,
        offset: offset
      }
    });
  }
  sortArticles(articles) {
    return [...articles].sort((a, b) => {
      const dateA = a.createdAt() ? a.createdAt().getTime() : 0;
      const dateB = b.createdAt() ? b.createdAt().getTime() : 0;
      return dateB - dateA;
    });
  }
  async loadArticles() {
    try {
      const articles = await this.fetchArticles(0);
      this.articles = this.sortArticles(articles);
      this.offset = articles.length;
      this.hasMore = articles.length === this.pageSize;
    } catch (error) {
      console.error('PinoyTalks Blog error:', error);
      this.error = (error == null ? void 0 : error.message) || 'Unable to load the OFW Blog articles. Please try again.';
    } finally {
      this.loading = false;
      m.redraw();
    }
  }
  async loadMoreArticles() {
    if (this.loadingMore || !this.hasMore) {
      return;
    }
    this.loadingMore = true;
    this.loadMoreError = null;
    try {
      const articles = await this.fetchArticles(this.offset);
      const merged = [...this.articles, ...articles];
      const uniqueArticles = Array.from(new Map(merged.map(article => [article.id(), article])).values());
      this.articles = this.sortArticles(uniqueArticles);
      this.offset += articles.length;
      if (articles.length < this.pageSize) {
        this.hasMore = false;
      }
    } catch (error) {
      console.error('PinoyTalks Blog load more error:', error);
      this.loadMoreError = 'Unable to load more articles. Please try again.';
    } finally {
      this.loadingMore = false;
      m.redraw();
    }
  }
  getFeaturedImage(article) {
    const firstPost = article.firstPost();
    if (!firstPost || !firstPost.contentHtml()) {
      return null;
    }
    const document = new DOMParser().parseFromString(firstPost.contentHtml(), 'text/html');
    const image = document.querySelector('img');
    return image ? image.getAttribute('src') : null;
  }
  getExcerpt(article) {
    const firstPost = article.firstPost();
    if (!firstPost || !firstPost.contentPlain()) {
      return '';
    }
    const content = firstPost.contentPlain().trim();
    return content.length > 190 ? content.substring(0, 190).trim() + '...' : content;
  }
  getCategoryTag(article) {
    const tags = typeof article.tags === 'function' ? article.tags() || [] : [];
    return tags.find(tag => tag.slug() !== 'ofw-blog') || null;
  }
  getCategory(article) {
    const category = this.getCategoryTag(article);
    return category ? category.name() : 'OFW BLOG';
  }
  getCategories() {
    const categories = new Map();
    this.articles.forEach(article => {
      const category = this.getCategoryTag(article);
      if (category) {
        categories.set(category.slug(), {
          slug: category.slug(),
          name: category.name()
        });
      }
    });
    return Array.from(categories.values()).sort((a, b) => a.name.localeCompare(b.name));
  }
  getFilteredArticles() {
    const query = this.searchQuery.trim().toLowerCase();
    return this.articles.filter(article => {
      const category = this.getCategoryTag(article);
      const matchesCategory = this.selectedCategory === 'all' || category && category.slug() === this.selectedCategory;
      if (!matchesCategory) {
        return false;
      }
      if (!query) {
        return true;
      }
      const title = article.title().toLowerCase();
      const excerpt = this.getExcerpt(article).toLowerCase();
      return title.includes(query) || excerpt.includes(query);
    });
  }
  async shareArticle(article) {
    const url = new URL(app.route.discussion(article), window.location.origin).href;
    const shareData = {
      title: article.title(),
      text: article.title(),
      url: url
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch (error) {
        if ((error == null ? void 0 : error.name) === 'AbortError') {
          return;
        }
        console.error('Native share failed:', error);
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      this.showCopiedMessage(article);
      return;
    } catch (error) {
      console.error('Clipboard API failed:', error);
    }
    try {
      const textarea = document.createElement('textarea');
      textarea.value = url;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      this.showCopiedMessage(article);
    } catch (error) {
      console.error('Fallback copy failed:', error);
      window.prompt('Copy this article link:', url);
    }
  }
  showCopiedMessage(article) {
    this.copiedArticleId = article.id();
    m.redraw();
    setTimeout(() => {
      if (this.copiedArticleId === article.id()) {
        this.copiedArticleId = null;
        m.redraw();
      }
    }, 2000);
  }
  articleCard(article) {
    const featuredImage = this.getFeaturedImage(article);
    const excerpt = this.getExcerpt(article);
    const articleUrl = app.route.discussion(article);
    const createdAt = article.createdAt();
    const date = createdAt ? createdAt.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }) : '';
    return m("article", {
      className: "PinoyTalksBlogCard"
    }, m("a", {
      className: "PinoyTalksBlogCard-image",
      href: articleUrl,
      "aria-label": article.title()
    }, featuredImage ? m("img", {
      src: featuredImage,
      alt: article.title(),
      loading: "lazy"
    }) : m("div", {
      className: "PinoyTalksBlogCard-placeholder"
    }, m("span", null, "PinoyTalks"), m("strong", null, "OFW BLOG"))), m("div", {
      className: "PinoyTalksBlogCard-content"
    }, m("div", {
      className: "PinoyTalksBlogCard-category"
    }, this.getCategory(article)), m("h2", {
      className: "PinoyTalksBlogCard-title"
    }, m("a", {
      href: articleUrl
    }, article.title())), date && m("div", {
      className: "PinoyTalksBlogCard-date"
    }, date), excerpt && m("p", {
      className: "PinoyTalksBlogCard-excerpt"
    }, excerpt), m("div", {
      className: "PinoyTalksBlogCard-actions"
    }, m("a", {
      className: "PinoyTalksBlogCard-read",
      href: articleUrl
    }, "Read Article \u2192"), m("button", {
      type: "button",
      className: "PinoyTalksBlogCard-share",
      onclick: () => this.shareArticle(article),
      "aria-label": 'Share ' + article.title()
    }, m("i", {
      className: "fas fa-share-alt",
      "aria-hidden": "true"
    }), m("span", null, this.copiedArticleId === article.id() ? 'Link Copied!' : 'Share')))));
  }
  view() {
    return m("div", {
      className: "PinoyTalksBlogPage"
    }, m("div", {
      className: "container PinoyTalksBlogContainer"
    }, m("header", {
      className: "PinoyTalksBlogHeader"
    }, m("div", {
      className: "PinoyTalksBlogHeader-brand"
    }, "PINOYTALKS"), m("h1", null, "OFW Blog"), m("p", null, "Guides, news, updates, and practical information for Filipino OFWs in Saudi Arabia.")), this.loading ? m("div", {
      className: "PinoyTalksBlogStatus"
    }, m(LoadingIndicator, null)) : this.error ? m("div", {
      className: "PinoyTalksBlogStatus"
    }, this.error) : this.articles.length === 0 ? m("div", {
      className: "PinoyTalksBlogStatus"
    }, m("h2", null, "No articles yet"), m("p", null, "Discussions tagged with OFW Blog will automatically appear here.")) : m("div", null, m("div", {
      className: "PinoyTalksBlogSearch"
    }, m("span", {
      className: "PinoyTalksBlogSearch-icon"
    }, "\uD83D\uDD0D"), m("input", {
      type: "search",
      placeholder: "Search OFW articles...",
      value: this.searchQuery,
      oninput: event => {
        this.searchQuery = event.target.value;
      },
      "aria-label": "Search OFW articles"
    }), this.searchQuery && m("button", {
      type: "button",
      className: "PinoyTalksBlogSearch-clear",
      onclick: () => {
        this.searchQuery = '';
      },
      "aria-label": "Clear search"
    }, "\xD7")), m("div", {
      className: "PinoyTalksBlogFilters"
    }, m("button", {
      type: "button",
      className: this.selectedCategory === 'all' ? 'PinoyTalksBlogFilter is-active' : 'PinoyTalksBlogFilter',
      onclick: () => {
        this.selectedCategory = 'all';
      }
    }, "All"), this.getCategories().map(category => m("button", {
      type: "button",
      className: this.selectedCategory === category.slug ? 'PinoyTalksBlogFilter is-active' : 'PinoyTalksBlogFilter',
      onclick: () => {
        this.selectedCategory = category.slug;
      }
    }, category.name))), m("div", {
      className: "PinoyTalksBlogGrid"
    }, this.getFilteredArticles().map(article => this.articleCard(article))), this.hasMore && m("div", {
      className: "PinoyTalksBlogLoadMore"
    }, m("button", {
      type: "button",
      disabled: this.loadingMore,
      onclick: () => this.loadMoreArticles()
    }, this.loadingMore ? 'Loading...' : 'Load More Articles')), this.loadMoreError && m("div", {
      className: "PinoyTalksBlogLoadMore-error"
    }, this.loadMoreError))));
  }
}
flarum.reg.add('pinoytalks-blog', 'forum/components/BlogPage', BlogPage);